import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";

import Welcome from "@/pages/Welcome";
import Pricing from "@/pages/Pricing";
import AuthCallback from "@/pages/AuthCallback";
import PickRole from "@/pages/PickRole";
import Subscribe from "@/pages/Subscribe";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import Onboarding from "@/pages/Onboarding";
import LinkPractitioner from "@/pages/LinkPractitioner";
import Dashboard from "@/pages/Dashboard";
import NewSession from "@/pages/NewSession";
import Recording from "@/pages/Recording";
import WriteNotes from "@/pages/WriteNotes";
import Processing from "@/pages/Processing";
import ReviewSummary from "@/pages/ReviewSummary";
import Results from "@/pages/Results";
import Sessions from "@/pages/Sessions";
import Feedback from "@/pages/Feedback";
import JournalList, { JournalNew, JournalDetail } from "@/pages/Journal";
import Settings from "@/pages/Settings";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center text-[#4A5D52]">Loading…</div>
);

const Protected = ({ children, requireSubscription = true, requireRole = true }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (requireRole && !user.role) return <Navigate to="/pick-role" replace />;
  if (requireSubscription && user.subscription_status !== "active") return <Navigate to="/subscribe" replace />;
  return children;
};

const ClientOnly = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role === "client" && !user.linked_practitioner_id) {
    return <Navigate to="/settings" replace />;
  }
  return children;
};

const Router = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/pick-role" element={
        <Protected requireSubscription={false} requireRole={false}><PickRole /></Protected>
      } />
      <Route path="/subscribe" element={
        <Protected requireSubscription={false}><Subscribe /></Protected>
      } />
      <Route path="/subscription-success" element={
        <Protected requireSubscription={false}><SubscriptionSuccess /></Protected>
      } />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/link-practitioner" element={<Protected><LinkPractitioner /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/new-session" element={<Protected><NewSession /></Protected>} />
      <Route path="/record" element={<Protected><Recording /></Protected>} />
      <Route path="/write-notes" element={<Protected><WriteNotes /></Protected>} />
      <Route path="/processing" element={<Protected><Processing /></Protected>} />
      <Route path="/review/:sessionId" element={<Protected><ReviewSummary /></Protected>} />
      <Route path="/results/:sessionId" element={<Protected><Results /></Protected>} />
      <Route path="/sessions" element={<Protected><Sessions /></Protected>} />
      <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
      <Route path="/journal" element={<Protected><JournalList /></Protected>} />
      <Route path="/journal/new" element={<Protected><JournalNew /></Protected>} />
      <Route path="/journal/:entryId" element={<Protected><JournalDetail /></Protected>} />
      <Route path="/settings" element={<Protected requireSubscription={false}><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Router />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
